// src/components/admin/GestionRegimenesJubilacion.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatosMaestrosService from '../../services/datosMaestrosService';
import MenuAccionesFila from './MenuAccionesFila';
import ModalDocumentacionRegimen from './ModalDocumentacionRegimen';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import { useAuth } from '../../auth/AuthContext';
import '../../style/components/GestionDatosMaestros.css';
import { coincideBusqueda } from '../../utils/textoComparable';

// Regímenes diferenciales del TRÁMITE de Jubilación (las opciones del desplegable "Régimen
// diferencial"). No confundir con "Regímenes Diferenciales" de la calculadora de aportes.
//
// Los 5 de sistema tienen documentación fija y no se tocan. Los agregados por el estudio nacen con
// la documentación genérica y acá se les arma la específica (cualquier rol operativo); renombrar,
// desactivar y eliminar queda para quien edita Datos Maestros.
const GestionRegimenesJubilacion = () => {
    const { puede } = useAuth();
    const puedeEditar = puede('datosMaestros.editar');

    const [regimenes, setRegimenes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selected, setSelected] = useState(null);
    const [nombre, setNombre] = useState('');
    const [regimenDocumentacion, setRegimenDocumentacion] = useState(null);
    const [filterNombre, setFilterNombre] = useState('');

    // Avisos con toast (al costado), como en Tipos de Turno.
    const mostrarAlerta = (message, variant) => {
        if (variant === 'success') {
            toast.success(message);
        } else {
            toast.error(message);
        }
    };

    const cargarRegimenes = useCallback(async () => {
        setLoading(true);
        try {
            // false: el ABM también lista los inactivos para poder reactivarlos.
            const response = await DatosMaestrosService.getRegimenesJubilacion(false);
            setRegimenes(response.data);
        } catch {
            mostrarAlerta('Error al cargar los regímenes', 'danger');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarRegimenes();
    }, [cargarRegimenes]);

    const handleOpenModal = (mode, regimen = null) => {
        setModalMode(mode);
        setSelected(mode === 'edit' ? regimen : null);
        setNombre(mode === 'edit' && regimen ? regimen.nombre : '');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelected(null);
        setNombre('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createRegimenJubilacion({ nombre });
                mostrarAlerta('Régimen creado. Nace con la documentación genérica: agregale la específica desde "Documentación".', 'success');
            } else {
                await DatosMaestrosService.updateRegimenJubilacion(selected.id, { nombre });
                mostrarAlerta('Régimen actualizado', 'success');
            }
            handleCloseModal();
            cargarRegimenes();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || 'Error al guardar el régimen', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleCambiarEstado = async (regimen) => {
        try {
            await DatosMaestrosService.updateRegimenJubilacion(regimen.id, { activo: !regimen.activo });
            cargarRegimenes();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || 'Error al cambiar el estado del régimen', 'danger');
        }
    };

    const handleDelete = async (regimen) => {
        if (!window.confirm(`¿Eliminar el régimen "${regimen.nombre}"?`)) return;
        setLoading(true);
        try {
            await DatosMaestrosService.deleteRegimenJubilacion(regimen.id);
            mostrarAlerta('Régimen eliminado', 'success');
            cargarRegimenes();
        } catch (error) {
            // El backend explica por qué no se puede (régimen en uso) y sugiere desactivarlo.
            mostrarAlerta(error?.response?.data?.message || 'Error al eliminar el régimen', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentacionGuardada = ({ tramitesActualizados }) => {
        setRegimenDocumentacion(null);
        mostrarAlerta(
            tramitesActualizados > 0
                ? `Documentación guardada correctamente. Se actualizó el checklist de ${tramitesActualizados} ${tramitesActualizados === 1 ? 'trámite' : 'trámites'}.`
                : 'Documentación guardada correctamente.',
            'success'
        );
        cargarRegimenes();
    };

    const regimenesFiltrados = regimenes.filter(r => coincideBusqueda(r.nombre, filterNombre));

    return (
        <div className="gestion-datos-maestros">
            <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
                <Button variant="primary" onClick={() => handleOpenModal('create')}>
                    <i className="bi bi-plus-circle me-2"></i>
                    Nuevo Régimen
                </Button>
            </div>

            <PanelFiltros
                hayFiltros={filterNombre.trim() !== ''}
                onLimpiar={() => setFilterNombre('')}
                resumen={`Mostrando ${regimenesFiltrados.length} de ${regimenes.length} regímenes`}
            >
                <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-regimen-jubilacion" ancho>
                    <Form.Control
                        id="filtro-regimen-jubilacion"
                        className="panel-filtros-input"
                        type="text"
                        placeholder="Nombre del régimen…"
                        value={filterNombre}
                        onChange={(e) => setFilterNombre(e.target.value)}
                    />
                </CampoFiltro>
            </PanelFiltros>

            {loading && regimenes.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Documentación</th>
                            <th className="text-center">Trámites</th>
                            <th className="text-center">Activo</th>
                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {regimenesFiltrados.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center text-muted py-4">
                                    {regimenes.length === 0
                                        ? 'No hay regímenes registrados'
                                        : 'No se encontraron regímenes que coincidan con la búsqueda'}
                                </td>
                            </tr>
                        ) : (
                            regimenesFiltrados.map((regimen) => (
                                <tr key={regimen.id}>
                                    <td className="fw-semibold">
                                        {regimen.esSistema ? (
                                            regimen.nombre
                                        ) : (
                                            <Button variant="link" className="p-0 fw-semibold text-start"
                                                onClick={() => setRegimenDocumentacion(regimen)}>
                                                {regimen.nombre}
                                            </Button>
                                        )}
                                        {regimen.esSistema && <Badge bg="secondary" className="ms-2">Sistema</Badge>}
                                    </td>
                                    <td>
                                        {regimen.esSistema ? (
                                            <span className="text-muted">Fija</span>
                                        ) : (
                                            <span>
                                                Genérica
                                                {regimen.formularios?.length > 0 && ` + ${regimen.formularios.length} específica${regimen.formularios.length === 1 ? '' : 's'}`}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <Badge bg={regimen.cantidadTramites > 0 ? 'info' : 'light'}
                                               text={regimen.cantidadTramites > 0 ? undefined : 'dark'}>
                                            {regimen.cantidadTramites ?? 0}
                                        </Badge>
                                    </td>
                                    <td className="text-center">
                                        <Form.Check
                                            type="switch"
                                            id={`regimen-jubilacion-activo-${regimen.id}`}
                                            checked={!!regimen.activo}
                                            onChange={() => handleCambiarEstado(regimen)}
                                            disabled={regimen.esSistema || !puedeEditar}
                                            title={regimen.activo ? 'Activo' : 'Inactivo'}
                                        />
                                    </td>
                                    <td className="text-end">
                                        {!regimen.esSistema && (
                                            <MenuAccionesFila acciones={[
                                                { etiqueta: 'Documentación', icono: 'bi-list-check', onClick: () => setRegimenDocumentacion(regimen) },
                                                puedeEditar && { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', regimen) },
                                                puedeEditar && { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(regimen) },
                                            ]} />
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nuevo Régimen de Jubilación' : 'Editar Régimen de Jubilación'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                required
                                maxLength={150}
                                placeholder="Ej: Trabajadores de la industria del vidrio"
                            />
                        </Form.Group>
                        {modalMode === 'create' && (
                            <Alert variant="info" className="mb-0 small">
                                Nace con la documentación genérica de régimen diferencial. La específica se
                                agrega después desde "Documentación".
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ModalDocumentacionRegimen
                show={Boolean(regimenDocumentacion)}
                regimen={regimenDocumentacion}
                onHide={() => setRegimenDocumentacion(null)}
                onGuardado={handleDocumentacionGuardada}
            />
        </div>
    );
};

export default GestionRegimenesJubilacion;
